import React from "react";
import { toast } from "react-toastify";

import { useAppForm } from "@/components/Forms";
import publishDeliverySchema, {
  type PublishDeliverySchemaType,
} from "@/schemas/DeliveryForm/publish-delivery";
import { formatDateTimeLocalInput } from "@/utils/date";
import {
  calculateMargin,
  getClientPrice,
  getDriverPricesFromClientPrice,
  getInitialDriverPrices,
  MIN_MARGIN_THRESHOLD,
} from "@/utils/delivery-pricing";
import { buildPublishPricing } from "@/utils/delivery-publish";

import type { Delivery } from "../types";
import { publishDeliveryFormDefaultValues } from "../types";
import {
  buildAllowDeliveryInput,
  buildAssignDriverInput,
  useCreateDeliveryDealMutations,
} from "./use-create-delivery-deal-mutations";

type UseDeliveryDealFormOptions = {
  delivery: Delivery | undefined;
};

export function useDeliveryDealForm({ delivery }: UseDeliveryDealFormOptions) {
  const [marginWarningOpen, setMarginWarningOpen] = React.useState(false);
  const [pendingForm, setPendingForm] = React.useState<PublishDeliverySchemaType | null>(null);

  const { allowDelivery, assignDriverToDelivery } = useCreateDeliveryDealMutations({ delivery });

  const submit = React.useCallback(
    (value: PublishDeliverySchemaType) => {
      if (!delivery) {
        toast.error("Impossible de publier: livraison introuvable", { position: "bottom-right" });
        return;
      }

      const pricing = buildPublishPricing(value, delivery);
      const driverId = value.selectedDriverId ?? undefined;

      if (driverId != null) {
        assignDriverToDelivery(buildAssignDriverInput(delivery.id, driverId, pricing));
        return;
      }

      allowDelivery(buildAllowDeliveryInput(delivery.id, pricing));
    },
    [allowDelivery, assignDriverToDelivery, delivery]
  );

  const form = useAppForm({
    defaultValues: publishDeliveryFormDefaultValues,
    validators: {
      onSubmit: publishDeliverySchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = publishDeliverySchema.safeParse(value);
      if (!parsed.success) {
        return;
      }

      const margin = calculateMargin(
        parsed.data.clientFinalPrice ?? 0,
        parsed.data.wantedPrice ?? 0
      );
      if (margin < MIN_MARGIN_THRESHOLD) {
        setPendingForm(parsed.data);
        setMarginWarningOpen(true);
        return;
      }

      submit(parsed.data);
    },
  });

  React.useEffect(() => {
    if (!delivery) return;

    form.setFieldValue(
      "carAvailabilityDate",
      formatDateTimeLocalInput(delivery.carAvailabilityDate)
    );
    form.setFieldValue("carPickUpDate", formatDateTimeLocalInput(delivery.carPickUpDate));
    form.setFieldValue(
      "carDeliveryDeadline",
      formatDateTimeLocalInput(delivery.carDeliveryDeadline)
    );
    form.setFieldValue("id", delivery.id);
    form.setFieldValue("selectedDriverId", delivery.selectedDriver?.id ?? null);

    const clientPrice = getClientPrice(delivery);
    form.setFieldValue("clientFinalPrice", clientPrice);

    const { min, max, median, wanted } = getInitialDriverPrices(delivery, clientPrice);
    form.setFieldValue("minPrice", min);
    form.setFieldValue("maxPrice", max);
    form.setFieldValue("medianPrice", median);
    form.setFieldValue("wantedPrice", wanted);
  }, [delivery, form.setFieldValue]);

  const handleClientPriceChange = React.useCallback(
    (clientPrice: number) => {
      const { min, max, median, wanted } = getDriverPricesFromClientPrice(clientPrice);
      form.setFieldValue("maxPrice", max);
      form.setFieldValue("minPrice", min);
      form.setFieldValue("medianPrice", median);
      form.setFieldValue("wantedPrice", wanted);
    },
    [form.setFieldValue]
  );

  const handleMarginWarningConfirm = React.useCallback(() => {
    if (pendingForm) {
      submit(pendingForm);
    }
    setPendingForm(null);
    setMarginWarningOpen(false);
  }, [pendingForm, submit]);

  const handleMarginWarningCancel = React.useCallback(() => {
    setPendingForm(null);
    setMarginWarningOpen(false);
  }, []);

  return {
    form,
    marginWarningOpen,
    handleClientPriceChange,
    handleMarginWarningConfirm,
    handleMarginWarningCancel,
  };
}

export type DeliveryDealFormApi = ReturnType<typeof useDeliveryDealForm>["form"];
